export type Point = { x: number; y: number };

export type Playfield = {
  width: number;
  height: number;
  margin: number;
};

/**
 * Bullet Hell Monday controls (correct grab logic):
 *
 * pointerdown  → lock offset = ship − finger; do NOT move ship
 * pointermove  → target = finger + offset (1:1 drag)
 * pointerup    → release
 * step         → ease ship toward target (no teleport)
 *
 * Never rewrite offset on grab — that caused random jumps.
 */
export class ThumbSteer {
  private active = false;
  private pointerId: number | null = null;
  private grabOffset: Point = { x: 0, y: 0 };
  target: Point = { x: 0, y: 0 };
  /** Cosmetic only (bank) */
  axis: Point = { x: 0, y: 0 };
  private prevTarget: Point = { x: 0, y: 0 };

  /** Snappy BHM follow without overshoot chaos */
  follow = 32;

  constructor(private field: Playfield) {
    this.center();
  }

  setField(field: Playfield): void {
    this.field = field;
    this.target = clampPoint(this.target, field);
  }

  center(): void {
    this.active = false;
    this.pointerId = null;
    this.grabOffset = { x: 0, y: 0 };
    this.axis = { x: 0, y: 0 };
    this.target = {
      x: this.field.width / 2,
      y: this.field.height * 0.72,
    };
    this.prevTarget = { ...this.target };
  }

  /**
   * Grab: remember finger→ship offset. Ship stays put on down.
   */
  onPointerDown(id: number, x: number, y: number, ship: Point): void {
    if (this.active) return;
    this.active = true;
    this.pointerId = id;
    this.grabOffset = {
      x: ship.x - x,
      y: ship.y - y,
    };
    // Critical: target equals current ship — zero jump on touch
    this.target = clampPoint({ x: ship.x, y: ship.y }, this.field);
    this.prevTarget = { ...this.target };
  }

  onPointerMove(id: number, x: number, y: number): void {
    if (!this.active || this.pointerId !== id) return;
    this.target = clampPoint(
      {
        x: x + this.grabOffset.x,
        y: y + this.grabOffset.y,
      },
      this.field,
    );
  }

  onPointerUp(id: number): void {
    if (this.pointerId !== id) return;
    this.active = false;
    this.pointerId = null;
    this.axis = { x: 0, y: 0 };
  }

  get isActive(): boolean {
    return this.active;
  }

  step(current: Point, dt: number): Point {
    const k = 1 - Math.exp(-this.follow * dt);
    const next = {
      x: current.x + (this.target.x - current.x) * k,
      y: current.y + (this.target.y - current.y) * k,
    };
    const dx = this.target.x - this.prevTarget.x;
    const dy = this.target.y - this.prevTarget.y;
    this.prevTarget = { ...this.target };
    if (Math.hypot(dx, dy) > 0.5) {
      this.axis = {
        x: clamp(dx / 36, -1, 1),
        y: clamp(dy / 36, -1, 1),
      };
    } else {
      this.axis = { x: this.axis.x * 0.8, y: this.axis.y * 0.8 };
    }
    return clampPoint(next, this.field);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function clampPoint(p: Point, field: Playfield): Point {
  const m = field.margin;
  return {
    x: Math.min(field.width - m, Math.max(m, p.x)),
    y: Math.min(field.height - m, Math.max(m, p.y)),
  };
}
